import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import Icon from "../../../base/components/Icon";
import type { ChipDropdownItem } from "../Chip";
import { Chip } from "../Chip";
import menuStyles from "../ButtonDropdown/ButtonDropdown.module.scss";
import { cx } from "../../utils/cx";
import narrowStyles from "./TableToolbarNarrowGroups.module.scss";
import type { TableToolbarAction, TableToolbarFilter } from "./TableToolbar";

const ChevronDownIcon = () => <Icon icon="chevron-down" />;

type ToolbarMode = "title" | "filterable" | "active-filters" | "selection";

export interface ToolbarMobileFiltersMenuProps {
    filters: TableToolbarFilter[];
    mode: ToolbarMode;
    showMoreFilters: boolean;
    moreFiltersMenuItems?: ChipDropdownItem[];
    onMoreFiltersClick?: () => void;
    onClearAllFilters?: () => void;
    showFilterableClear: boolean;
}

function filterTriggerCounter(filters: TableToolbarFilter[]) {
    const active = filters.filter(f => f.active);
    if (!active.length) return undefined;
    const numeric = active.reduce(
        (sum, f) => sum + (typeof f.count === "number" ? f.count : 0),
        0,
    );
    return numeric > 0 ? numeric : active.length;
}

function buildFilterMenuNodes(filters: TableToolbarFilter[]): ReactNode[] {
    const out: ReactNode[] = [];
    filters.forEach(filter => {
        const chunk: ReactNode[] = [];

        if (filter.active && filter.onRemove) {
            chunk.push(
                <DropdownMenu.Item
                    className={menuStyles.item}
                    key={`${filter.id}-clear`}
                    onSelect={() => {
                        filter.onRemove?.();
                    }}
                >
                    {`Clear ${filter.label}`}
                </DropdownMenu.Item>,
            );
        } else {
            const hasMenu =
                Boolean(filter.menuItems?.length) && !filter.onRemove;
            if (hasMenu && filter.menuItems) {
                chunk.push(
                    <DropdownMenu.Label
                        className={narrowStyles.menuLabel}
                        key={`${filter.id}-label`}
                    >
                        {filter.label}
                    </DropdownMenu.Label>,
                );
                filter.menuItems.forEach(item => {
                    chunk.push(
                        <DropdownMenu.Item
                            className={cx(
                                menuStyles.item,
                                item.destructive && menuStyles.itemDestructive,
                            )}
                            disabled={item.disabled}
                            key={`${filter.id}-${item.id}`}
                            onSelect={() => item.onSelect?.(item.id)}
                        >
                            {item.label}
                        </DropdownMenu.Item>,
                    );
                });
            } else if (filter.onClick) {
                chunk.push(
                    <DropdownMenu.Item
                        className={menuStyles.item}
                        key={filter.id}
                        onSelect={() => filter.onClick?.()}
                    >
                        {filter.label}
                    </DropdownMenu.Item>,
                );
            }
        }

        if (!chunk.length) return;
        if (out.length) {
            out.push(
                <DropdownMenu.Separator
                    className={narrowStyles.separator}
                    key={`sep-f-${filter.id}`}
                />,
            );
        }
        out.push(...chunk);
    });
    return out;
}

/**
 * Mobile / narrow: one **Filters** chip opening a menu (no horizontal chip strip).
 */
export function ToolbarMobileFiltersMenu({
    filters,
    mode,
    showMoreFilters,
    moreFiltersMenuItems,
    onMoreFiltersClick,
    onClearAllFilters,
    showFilterableClear,
}: ToolbarMobileFiltersMenuProps) {
    const [open, setOpen] = useState(false);
    const counter = useMemo(() => filterTriggerCounter(filters), [filters]);

    const filterNodes = useMemo(() => buildFilterMenuNodes(filters), [filters]);

    const hasFilterEntries = filterNodes.length > 0;

    return (
        <div className={narrowStyles.root}>
            <DropdownMenu.Root onOpenChange={setOpen} open={open}>
                <DropdownMenu.Trigger asChild>
                    <Chip
                        color="default"
                        counter={counter}
                        iconRight={<ChevronDownIcon />}
                        label="Filters"
                        onClick={() => undefined}
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="start"
                        className={menuStyles.content}
                        loop
                        side="bottom"
                        sideOffset={6}
                    >
                        {filterNodes}
                        {showMoreFilters ? (
                            <>
                                {hasFilterEntries ? (
                                    <DropdownMenu.Separator
                                        className={narrowStyles.separator}
                                    />
                                ) : null}
                                {moreFiltersMenuItems?.length ? (
                                    <>
                                        <DropdownMenu.Label
                                            className={narrowStyles.menuLabel}
                                        >
                                            More filters
                                        </DropdownMenu.Label>
                                        {moreFiltersMenuItems.map(item => (
                                            <DropdownMenu.Item
                                                className={cx(
                                                    menuStyles.item,
                                                    item.destructive &&
                                                        menuStyles.itemDestructive,
                                                )}
                                                disabled={item.disabled}
                                                key={`more-${item.id}`}
                                                onSelect={() =>
                                                    item.onSelect?.(item.id)
                                                }
                                            >
                                                {item.label}
                                            </DropdownMenu.Item>
                                        ))}
                                    </>
                                ) : (
                                    <DropdownMenu.Item
                                        className={menuStyles.item}
                                        onSelect={() => onMoreFiltersClick?.()}
                                    >
                                        More filters…
                                    </DropdownMenu.Item>
                                )}
                            </>
                        ) : null}
                        {mode === "active-filters" && onClearAllFilters ? (
                            <>
                                <DropdownMenu.Separator
                                    className={narrowStyles.separator}
                                />
                                <DropdownMenu.Item
                                    className={menuStyles.item}
                                    onSelect={() => onClearAllFilters()}
                                >
                                    Clear all
                                </DropdownMenu.Item>
                            </>
                        ) : null}
                        {showFilterableClear && onClearAllFilters ? (
                            <>
                                <DropdownMenu.Separator
                                    className={narrowStyles.separator}
                                />
                                <DropdownMenu.Item
                                    className={menuStyles.item}
                                    onSelect={() => onClearAllFilters()}
                                >
                                    Clear
                                </DropdownMenu.Item>
                            </>
                        ) : null}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
}

export interface ToolbarMobileActionsMenuProps {
    actions: TableToolbarAction[];
}

function buildActionMenuNodes(actions: TableToolbarAction[]): ReactNode[] {
    const out: ReactNode[] = [];
    actions.forEach(action => {
        const chunk: ReactNode[] = [];
        if (action.menuItems?.length) {
            chunk.push(
                <DropdownMenu.Label
                    className={narrowStyles.menuLabel}
                    key={`${action.id}-label`}
                >
                    {action.label}
                </DropdownMenu.Label>,
            );
            action.menuItems.forEach(item => {
                chunk.push(
                    <DropdownMenu.Item
                        className={cx(
                            menuStyles.item,
                            item.destructive && menuStyles.itemDestructive,
                        )}
                        disabled={item.disabled}
                        key={`${action.id}-${item.id}`}
                        onSelect={() => item.onSelect?.(item.id)}
                    >
                        {item.label}
                    </DropdownMenu.Item>,
                );
            });
        } else {
            chunk.push(
                <DropdownMenu.Item
                    className={menuStyles.item}
                    key={action.id}
                    onSelect={() => action.onClick?.()}
                >
                    {action.label}
                </DropdownMenu.Item>,
            );
        }
        if (!chunk.length) return;
        if (out.length) {
            out.push(
                <DropdownMenu.Separator
                    className={narrowStyles.separator}
                    key={`sep-a-${action.id}`}
                />,
            );
        }
        out.push(...chunk);
    });
    return out;
}

/**
 * Mobile / narrow: one **Actions** chip opening a menu (no horizontal bulk strip).
 */
export function ToolbarMobileActionsMenu({
    actions,
}: ToolbarMobileActionsMenuProps) {
    const [open, setOpen] = useState(false);
    const nodes = useMemo(() => buildActionMenuNodes(actions), [actions]);

    return (
        <div className={narrowStyles.root}>
            <DropdownMenu.Root onOpenChange={setOpen} open={open}>
                <DropdownMenu.Trigger asChild>
                    <Chip
                        color="default"
                        iconRight={<ChevronDownIcon />}
                        label="Actions"
                        onClick={() => undefined}
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="start"
                        className={menuStyles.content}
                        loop
                        side="bottom"
                        sideOffset={6}
                    >
                        {nodes}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
}
